import { Box, Divider } from '@mui/material';
import type { ScanJob } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useFields } from '@/components/table/hooks';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { FieldsSection } from './fieldsSection';
import { JobItemFields } from './jobItemFields';
import { JobStatusCard } from './jobStatusCard';
import { scanJobItemFields, sourceLocationFields } from './scanJobItemFields';

export const ScanJobsCard = ({
  job,
  onClose,
}: {
  job: ScanJob | string;
  onClose: () => void;
}) => {
  return (
    <PanelWrapper
      header={{
        title: 'Job details',
        onClose,
        hideBorder: true,
      }}
      testIdPrefix='scan-jobs-card'
    >
      {typeof job === 'string' ? (
        <JobContentFromId jobId={job} onClose={onClose} />
      ) : (
        <ScanJobsCardContent job={job} />
      )}
    </PanelWrapper>
  );
};

const JobContentFromId = ({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) => {
  const { snackbar } = useWorkspace();
  const dal = useDAL();
  const { data: job, isLoading } = dal.jobs.scan.get(jobId);

  useEffect(() => {
    if (!isLoading && !job) {
      onClose();
      snackbar.showWarningMessage(
        'Looks like your link is broken and this job ID is invalid.'
      );
    }
  }, [job, isLoading, onClose, snackbar]);

  return isLoading || !job?.job ? (
    <Loader />
  ) : (
    <ScanJobsCardContent job={job.job} />
  );
};

const ScanJobsCardContent = ({ job }: { job: ScanJob }) => {
  const formattedFields = useFields(sourceLocationFields, job);

  return (
    <Box className='pb-[40px]' overflow='auto'>
      <JobItemFields job={job} fields={scanJobItemFields} />
      <Divider />
      <JobStatusCard job={job} />
      <FieldsSection
        defaultExpanded
        title='Source location'
        fields={formattedFields}
      />
    </Box>
  );
};
